import Image from 'next/image';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import { generateArticleSchema } from '../../utils/schemaGenerators';


export const metadata = {
  title: 'What to Know Before Renovating Your Bedroom',
  image: '/blog/bedroom-renovation-guide.webp',
  date: 'April 12, 2025',
  author: 'Modern Build Team',
  excerpt: 'Planning a bedroom remodel? Here’s what you need to consider before transforming your space—from lighting and layout to storage and comfort.',
  category: 'Bedroom Remodeling',
  tags: ['Bedroom Remodeling', 'Home Remodeling', 'Interior Design'],
};

export default function BedroomRenovationGuide() {
  return (
    <>
      <Head>
        <title>{metadata.title}</title>
        <meta name="title" content={metadata.title} />
        <meta name="description" content={metadata.excerpt} />
        <meta name="author" content={metadata.author} />
        <meta name="keywords" content={metadata.tags.join(', ')} />
        <link rel="canonical" href={`https://modernbuildremodel.com/blog/bedroom-renovation-guide`} />

        <meta property="og:title" content={metadata.title}/>
        <meta property="og:description" content={metadata.excerpt} />
        <meta property="og:image" content={`https://modernbuildremodel.com${metadata.image}`} />
        <meta property="og:url" content={`https://modernbuildremodel.com/blog/bedroom-renovation-guide`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metadata.title}/>
        <meta name="twitter:description" content={metadata.excerpt} />
        <meta name="twitter:image" content={`https://modernbuildremodel.com${metadata.image}`} />
        <meta name="twitter:site" content="@modernbuildhome" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://modernbuildremodel.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": `https://modernbuildremodel.com/blog`
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Bedroom Renovation Guide",
                  "item": `https://modernbuildremodel.com/blog/bedroom-renovation-guide`
                },
              ]
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateArticleSchema(metadata, 'bedroom-renovation-guide')),
          }}
        />
      </Head>

      <Header />

      <div className="bg-white">
        <Hero category="bedroomRenovationGuide" page="page" hasContentH1={false}/>

        <div className="container mx-auto px-4 py-10 max-w-4xl">
          <p className="text-sm text-black mb-2">
            <span className="capitalize text-primary tracking-[3px]">By {metadata.author} / {metadata.date}</span>
          </p>

          <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] mb-10 rounded-lg overflow-hidden">
            <Image
              src={metadata.image}
              alt={`Modern Build Home Remodeling post: ${metadata.title}`}
              title={metadata.title}
              fill
              sizes="(max-width: 768px) 100vw, 700px"
              className="object-cover rounded-lg"
            />
          </div>

          <div className="prose prose-lg max-w-none text-md text-black">
            <p>
              A bedroom renovation isn’t just about aesthetics—it's about creating a personalized sanctuary that supports your rest, wellness, and lifestyle. Unlike kitchens or bathrooms, bedroom remodeling is less about plumbing and appliances and more about atmosphere, layout, and comfort.
            </p>

            <h3>Start With the Purpose</h3>
            <p>
              Is your bedroom simply for sleep? Or do you need a space to work, unwind, or get ready in peace? Your answers will determine the priorities—whether that’s noise reduction, layered lighting, or smart closet design.
            </p>

            <h3>Key Features to Consider</h3>
            <p>
              Storage is often overlooked. Integrated wardrobes, under-bed drawers, and floating nightstands can help you maintain a clutter-free space. Lighting should be versatile—overhead for general use, task lighting for reading, and ambient options for relaxation.
            </p>

            <p>
              Materials matter too. Soft textures, acoustic insulation, blackout curtains, and calming color palettes all contribute to a more restful environment. Flooring can also change the vibe—think hardwood for elegance or plush carpet for cozy comfort.
            </p>

            <h3>Don't Forget Layout & Flow</h3>
            <p>
              A well-planned layout avoids dead corners and creates clear pathways between the bed, closet, and ensuite bath. Consider adding a small seating area if space allows—it can double as a quiet reading nook or morning coffee spot.
            </p>

            <p>
              Ultimately, a bedroom remodel should reflect your personality while also supporting better sleep and a calmer lifestyle. Modern Build specializes in designing bedroom spaces that balance function, beauty, and tranquility.
            </p>

            <p className="mt-8">
              Looking to upgrade your primary suite or guest room? Let our expert team turn your vision into a comfortable, stylish retreat that fits your needs.
            </p>
          </div>
        </div>

        <SocialProof />
        <ContactUs />
        <Footer />
      </div>
    </>
  );
}
